import { useEffect, useMemo, useState } from "react";
import { Box, Card, CardActionArea, CardContent, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { employeesApi } from "../../../api/employeesApi";
import { timeTrackingApi } from "../../../api/timeTrackingApi";
import { EmployeeUserType } from "../../../types/employeeType";
import { useLocale } from "../../../hooks/UseLocale";
import { usePermissionChecker } from "../../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../../types/permissionType";
import WorkTimeQuotaDialog from "../../employees/EmployeesTable/WorkTimeQuotaDialog";
import { companyManagementApi } from "../../../api/companyManagementApi";

type EmployeeQuota = {
  employee: EmployeeUserType;
  totalBalance: number | null;
  workedMinutesBalance: number | null;
  plannedWorkMinutes: number | null;
  pendingApprovals: number | null;
};

const TimeTrackingOverviewPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useLocale();
  const { serviceModuleId } = useParams<{ serviceModuleId: string }>();
  const parsedServiceModuleId = Number(serviceModuleId);
  const { userHasPermission } = usePermissionChecker();
  const canEditEmployees = userHasPermission(PermissionEntityType.Employees, "Create");
  const [employees, setEmployees] = useState<EmployeeUserType[]>([]);
  const [employeeQuotas, setEmployeeQuotas] = useState<EmployeeQuota[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [quotaDialogEmployee, setQuotaDialogEmployee] = useState<EmployeeUserType | null>(null);
  const [openQuotaDialog, setOpenQuotaDialog] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(parsedServiceModuleId)) return;
    const fetchData = async () => {
      setIsLoading(true);
      const employeeResult = await employeesApi.getServiceModuleEmployees(parsedServiceModuleId);
      if (employeeResult.success() && employeeResult.result) {
        setEmployees(employeeResult.result);
      } else {
        setEmployees([]);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [parsedServiceModuleId]);

  useEffect(() => {
    const fetchCompany = async () => {
      const company = await companyManagementApi.getCompany();
      if (company && typeof company.id === "number") {
        setCompanyId(company.id);
      }
    };
    fetchCompany();
  }, []);

  useEffect(() => {
    if (!Number.isFinite(parsedServiceModuleId) || employees.length === 0) {
      setEmployeeQuotas([]);
      return;
    }
    const fetchQuotas = async () => {
      const quotaResults = await Promise.all(
        employees.map(async (employee) => {
          const [currentResult, balanceResult, pendingResult] = await Promise.all([
            timeTrackingApi.getCurrentTimeQuota(parsedServiceModuleId, employee.id),
            timeTrackingApi.getTimeQuotaBalance(parsedServiceModuleId, employee.id),
            timeTrackingApi.getCompanyEntries(parsedServiceModuleId, {
              limit: 1,
              offset: 0,
              employeeUserId: employee.id,
              approval: "Pending",
            }),
          ]);
          return {
            employee,
            totalBalance: balanceResult.success() && balanceResult.result
              ? balanceResult.result.totalBalance
              : null,
            workedMinutesBalance: currentResult.success() && currentResult.result
              ? currentResult.result.workedMinutesBalance
              : null,
            plannedWorkMinutes: currentResult.success() && currentResult.result
              ? currentResult.result.plannedWorkMinutes
              : null,
            pendingApprovals: pendingResult.success() && pendingResult.result
              ? pendingResult.result.totalCount
              : null,
          };
        })
      );
      setEmployeeQuotas(quotaResults);
    };
    fetchQuotas();
  }, [employees, parsedServiceModuleId]);

  const toHours = (minutes: number) => Math.round((minutes / 60) * 100) / 100;

  const allEntriesPath = useMemo(() => {
    if (!Number.isFinite(parsedServiceModuleId)) return "";
    return `/${locale}/servicemodules/timetracking/${parsedServiceModuleId}/all`;
  }, [locale, parsedServiceModuleId]);

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardActionArea
              sx={{ height: "100%" }}
              onClick={() => {
                if (allEntriesPath) navigate(allEntriesPath);
              }}
            >
              <CardContent>
                <Typography variant="h6">
                  {t("serviceModulesPage.timetrackingPage.allEntriesCard.title")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("serviceModulesPage.timetrackingPage.allEntriesCard.subtitle")}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        {employeeQuotas.map(({
          employee,
          totalBalance,
          workedMinutesBalance,
          plannedWorkMinutes,
          pendingApprovals,
        }) => (
          <Grid item xs={12} sm={6} md={4} key={employee.id}>
            <Card sx={{ height: "100%" }}>
              <CardActionArea
                sx={{ height: "100%" }}
                onClick={() =>
                  navigate(
                    `/${locale}/servicemodules/timetracking/${parsedServiceModuleId}/employee/${employee.id}`
                  )
                }
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1">
                      {employee.firstName} {employee.lastName}
                    </Typography>
                    <Tooltip title={t("employeesPage.workTimeQuota.title")}>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          setQuotaDialogEmployee(employee);
                          setOpenQuotaDialog(true);
                        }}
                      >
                        <AccessTime fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {isLoading
                      ? t("serviceModulesPage.timetrackingPage.loadingEmployees")
                      : t("serviceModulesPage.timetrackingPage.currentWorked", {
                          hours: workedMinutesBalance === null ? "—" : toHours(workedMinutesBalance),
                        })}
                  </Typography>
                  {!isLoading && (
                    <>
                      <Typography variant="body2" color="textSecondary">
                        {t("serviceModulesPage.timetrackingPage.currentPlanned", {
                          hours: plannedWorkMinutes === null ? "—" : toHours(plannedWorkMinutes),
                        })}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            totalBalance === null
                              ? "text.secondary"
                              : totalBalance >= 0
                                ? "success.main"
                                : "error.main",
                        }}
                      >
                        {t("serviceModulesPage.timetrackingPage.currentDelta", {
                          hours: totalBalance === null ? "—" : toHours(totalBalance),
                        })}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {pendingApprovals === null
                          ? "—"
                          : t("serviceModulesPage.timetrackingPage.pendingApprovals", {
                              count: pendingApprovals,
                            })}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      {quotaDialogEmployee && (
        <WorkTimeQuotaDialog
          open={openQuotaDialog}
          onClose={() => {
            setOpenQuotaDialog(false);
            setQuotaDialogEmployee(null);
          }}
          employee={quotaDialogEmployee}
          timeTrackingServiceModuleId={parsedServiceModuleId}
          canEdit={canEditEmployees}
          companyId={companyId}
        />
      )}
    </Box>
  );
};

export default TimeTrackingOverviewPage;
